import React, { useMemo, useState } from "react";
import { AGENT_COLORS, detectAgentFromStatus, LogItem } from "../utils/tools";
import { CheckCircle2, Loader, Route, ClipboardList } from "lucide-react";
import { agents_icons } from "./agentsIcons";

const DEFAULT_ACCENT = "#EC10A3";
const fallbackColor = "#9ca3af";

const getAgentAccent = (agentName) =>
  (agentName && AGENT_COLORS[agentName]) || DEFAULT_ACCENT;

const StatusPill = ({ text }) => {
  const accent = DEFAULT_ACCENT;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "6px 0",
      }}
    >
      {/* Don't uncomment out the following -- its just for me to rememember a icon size and design */}
      {/* <div
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: "rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accent,
        }}
      >
        <Route size={13} />
      </div> */}
      <span
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(229,231,235,0.95), rgba(229,231,235,0.72))",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontSize: 12,
          whiteSpace: "pre-line",
          flex: 1,
          fontWeight: 300,
          textShadow: `0 0 16px ${accent}55, 0 0 8px ${accent}44`,
        }}
      >
        {text}
      </span>
    </div>
  );
};

const TimelineRow = ({ node, children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      position: "relative",
      padding: "6px 0",
    }}
  >
    {node}
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);

const TimelineNode = ({ state = "info", accent = DEFAULT_ACCENT, icon: Icon }) => {
  const size = 22;
  const isCompleted = state === "completed";
  const iconColor = isCompleted ? "#9ca3af" : accent;
  const IconComponent = isCompleted ? CheckCircle2 : Icon || Route;
  return (
    <div
      style={{
        marginTop: 0,
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: iconColor,
      }}
    >
      <IconComponent size={17} color={iconColor} />
    </div>
  );
};

const PlanBlock = ({ heading, steps }) => {
  const [hover, setHover] = useState(false);
  const accentPrimary = "rgba(233,61,130,0.16)";
  const accentSecondary = "rgba(124,58,237,0.12)";
  const baseShadow = "0 8px 32px rgba(0,0,0,0.12), 0 0 40px rgba(233,61,130,0.08)";
  const hoverShadow = "0 8px 32px rgba(0,0,0,0.16), 0 0 60px rgba(233,61,130,0.15)";

  return (
    <div className={`plan-block-wrapper${hover ? " is-hovered" : ""}`}>
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, rgba(14,14,20,0.65), rgba(10,10,14,0.55))",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 16,
          padding: "16px 18px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          boxShadow: hover ? hoverShadow : baseShadow,
          backdropFilter: "blur(16px)",
          transition: "all 0.35s ease",
          transform: hover ? "scale(1.01)" : "scale(1)",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(233,61,130,0.08), rgba(0,0,0,0), rgba(124,58,237,0.08))",
            opacity: hover ? 1 : 0,
            transition: "opacity 0.35s ease",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: "linear-gradient(90deg, transparent, rgba(233,61,130,0.3), transparent)",
            opacity: 0.9,
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${accentPrimary}, ${accentSecondary})`,
                border: "1px solid rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(6px)",
                transition: "transform 0.3s ease",
                transform: hover ? "scale(1.05)" : "scale(1)",
              }}
            >
              <ClipboardList size={16} color="#e5e7eb" />
            </div>
            <h3
              style={{
                margin: 0,
                color: "#e5e7eb",
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: "0.02em",
              }}
            >
              {heading || "Plan"}
            </h3>
          </div>

          <ol
            style={{
              margin: 0,
              paddingLeft: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              color: "#dcdfe6",
              fontSize: 13.5,
              fontWeight: 300,
              lineHeight: 1.55,
            }}
          >
            {steps.map((p, idx) => (
              <li
                key={p.id || p.text}
                style={{
                  listStyle: "none",
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  transition: "color 0.2s ease",
                }}
              >
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "999px",
                    background: "rgba(233,61,130,0.12)",
                    border: "1px solid rgba(233,61,130,0.25)",
                    color: "#f6f7fb",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </span>
                <span style={{ paddingTop: 2 }}>{p.text}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

const LegacyLog = ({ entry, accentColor }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      fontSize: "0.875rem",
      lineHeight: 1.35,
      color: "#9ca3af",
      padding: "4px 0",
    }}
  >
    <LogItem text={entry.text} accentColor={accentColor || fallbackColor} />
  </div>
);

export function LogsList({ logs = [], analysisComplete }) {
  const entries = Array.isArray(logs) ? logs : [];
  const timeline = [];
  let planParagraphs = [];
  let planHeading = "";

  const flushPlan = () => {
    if (planParagraphs.length === 0) return;
    timeline.push({
      kind: "plan",
      heading: planHeading,
      steps: planParagraphs,
    });
    planParagraphs = [];
    planHeading = "";
  };

  entries.forEach((entry, idx) => {
    const kind = entry?.kind || "text";
    switch (kind) {
      case "plan-heading":
        planHeading = entry?.text || "Planned Strategy";
        break;
      case "plan-paragraph":
        planParagraphs.push(entry);
        break;
      case "agent-status":
        flushPlan();
        timeline.push({
          kind: "agent",
          entry,
          isActive: idx === entries.length - 1 && !analysisComplete,
        });
        break;
      case "status-pill":
        flushPlan();
        timeline.push({ kind: "pill", entry });
        break;
      default:
        flushPlan();
        timeline.push({ kind: "legacy", entry });
        break;
    }
  });
  flushPlan();

  const spineStyle = {
    position: "absolute",
    left: 8,
    top: 0,
    bottom: 0,
    width: 3,
    background: "rgba(255,255,255,0.05)",
  };

  const renderNode = (node, idx) => {
    switch (node.kind) {
      case "plan":
        return (
          <TimelineRow
            key={`plan-${idx}`}
            node={
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.06)",
                  marginTop: 5,
                }}
              />
            }
          >
            <PlanBlock heading={node.heading} steps={node.steps} />
          </TimelineRow>
        );
      case "agent": {
        const agentName =
          node.entry?.meta?.agentName ||
          detectAgentFromStatus(node.entry?.text) ||
          "Agent";
        const state = node.entry?.meta?.state || (node.isActive ? "running" : "info");
        const color = getAgentAccent(agentName);
        const runningCount = node.entry?.meta?.runningCount || 0;
        const totalCount = node.entry?.meta?.totalCount || runningCount;
        const agentMeta = agents_icons.find((a) => a.name === agentName);
        const IconComponent = agentMeta?.icon || Loader;
        const detail =
          state === "running"
            ? runningCount > 1
              ? `Running ${runningCount} tasks`
              : "Running 1 task"
            : state === "completed"
            ? totalCount > 1
              ? `Completed ${totalCount} tasks`
              : "Completed"
            : node.entry?.text;

        return (
          <TimelineRow
            key={node.entry.id || `${idx}-agent`}
            node={<TimelineNode state={state} accent={color} icon={IconComponent} />}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  color: "#f3f4f6",
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                }}
              >
                {agentName}
              </span>
              <span style={{ color: "#9ca3af", fontSize: 12 }}>{detail}</span>
            </div>
          </TimelineRow>
        );
      }
      case "pill": {
        return (
          <TimelineRow
            key={node.entry.id || `${idx}-pill`}
            node={<TimelineNode state="info" accent={DEFAULT_ACCENT} icon={Route} />}
          >
            <StatusPill text={node.entry.text} />
          </TimelineRow>
        );
      }
      case "legacy":
      default:
        return (
          <TimelineRow
            key={node.entry.id || `${idx}-legacy`}
            node={<TimelineNode state="info" accent={fallbackColor} />}
          >
            <LegacyLog entry={node.entry} accentColor={fallbackColor} />
          </TimelineRow>
        );
    }
  };

  return (
    <div style={{ position: "relative", paddingLeft: 18, background: "transparent" }}>
      <div style={spineStyle} />
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {timeline.map((n, idx) => renderNode(n, idx))}
      </div>
    </div>
  );
}
